import React from "react";
import { motion } from "framer-motion";
import {
  ReceiptText, Stamp, Languages, Building2, FileText, Globe,
  ClipboardList, Car, ArrowRight, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const iconMap = {
  ReceiptText, Stamp, Languages, Building2, FileText, Globe, ClipboardList, Car,
};

const ease = [0.22, 1, 0.36, 1];

const ServiceCard = ({ item, index, onRequest }) => {
  const Icon = iconMap[item.icon] || FileText;
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, ease, delay: (index % 4) * 0.06 }}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white p-6 shadow-[var(--shadow-sm)] transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[color:var(--border-strong)] hover:shadow-[var(--shadow-md)] ${
        item.featured
          ? "border-l-4 border-l-[var(--brand-burgundy)] border-y-[color:var(--border-subtle)] border-r-[color:var(--border-subtle)]"
          : "border-[color:var(--border-subtle)]"
      }`}
      data-testid={`services-${item.key}-card`}
    >
      {item.featured && (
        <div className="pointer-events-none absolute inset-0 bg-[image:var(--burgundy-sheen)]" />
      )}
      <div className="relative flex items-center justify-between">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface-2)] text-[var(--brand-charcoal)] transition-colors group-hover:bg-[rgba(139,30,63,0.08)] group-hover:text-[var(--brand-burgundy)]">
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        </span>
        {item.featured && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-burgundy)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
            <Sparkles className="h-3 w-3" /> {t.services.flagshipBadge}
          </span>
        )}
      </div>
      <h3 className="relative mt-4 font-heading text-xl font-semibold text-[var(--brand-navy)]">{item.title}</h3>
      <p className="relative mt-2 flex-1 text-sm leading-relaxed text-[var(--muted-ink)]">{item.desc}</p>
      <button
        onClick={onRequest}
        className="relative mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-burgundy)] transition-colors hover:text-[var(--brand-burgundy-dark)]"
        data-testid={`services-${item.key}-request`}
      >
        {t.services.learnMore}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
    </motion.div>
  );
};

export const Services = () => {
  const { t } = useLanguage();
  const cs = t.services.comingSoon;
  const ComingIcon = iconMap[cs.icon] || Car;

  const goPortal = () => {
    const el = document.getElementById("portal");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="relative bg-[var(--brand-offwhite)] py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand-burgundy)]">{t.services.title}</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[var(--brand-navy)] sm:text-4xl">
            {t.services.subtitle}
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.services.items.map((item, i) => (
            <ServiceCard key={item.key} item={item} index={i} onRequest={goPortal} />
          ))}

          {/* Coming Soon card */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, ease }}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[var(--brand-charcoal)] p-6 text-[var(--brand-offwhite)] shadow-[var(--shadow-md)] transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-white/25 sm:col-span-2 lg:col-span-1"
            data-testid="services-coming-soon-card"
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white">
                <ComingIcon className="h-6 w-6" strokeWidth={1.75} />
              </span>
              <span className="rounded-full bg-[var(--brand-burgundy)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                {t.services.comingSoonBadge}
              </span>
            </div>
            <h3 className="mt-4 font-heading text-xl font-semibold text-white">{cs.title}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-white/70">{cs.desc}</p>
            <Button
              onClick={goPortal}
              className="mt-4 h-10 rounded-xl bg-white text-sm font-semibold text-[var(--brand-charcoal)] transition-colors hover:bg-white/90"
              data-testid="services-waitlist-cta"
            >
              {t.services.waitlistCta}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
